import { useEffect, useMemo, useState } from "react";

import { resolveImageSourceForPixelRead } from "../native/imageDataUrl";
import {
  averageImageDataColor,
  buildImageGradientPalette,
} from "../utils/imageGradientPalette";

const sampledColorCache = new Map();
const pendingSampleCache = new Map();
const MAX_CACHED_SAMPLES = 128;
const IMAGE_GRADIENT_SAMPLE_TIMEOUT_MS = 10000;

const normalizeImageSource = (src) => String(src || "").trim();

const rememberSample = (src, sample) => {
  if (!src || !sample) return;
  if (sampledColorCache.has(src)) sampledColorCache.delete(src);
  sampledColorCache.set(src, sample);

  while (sampledColorCache.size > MAX_CACHED_SAMPLES) {
    const oldestKey = sampledColorCache.keys().next().value;
    sampledColorCache.delete(oldestKey);
  }
};

export const getCachedImageGradientSample = (src) =>
  sampledColorCache.get(normalizeImageSource(src));

export function loadImageGradientSample(src) {
  const normalizedSrc = normalizeImageSource(src);
  if (!normalizedSrc || typeof window === "undefined" || typeof Image === "undefined") {
    return Promise.resolve(null);
  }

  const cached = sampledColorCache.get(normalizedSrc);
  if (cached) return Promise.resolve(cached);

  const pending = pendingSampleCache.get(normalizedSrc);
  if (pending) return pending;

  let timeoutId = null;
  const sampleRequest = (async () => {
    try {
      const pixelReadableSrc = await resolveImageSourceForPixelRead(normalizedSrc);
      if (!pixelReadableSrc) return null;

      const image = await new Promise((resolve, reject) => {
        const nextImage = new Image();
        if (/^https?:\/\//i.test(pixelReadableSrc)) {
          nextImage.crossOrigin = "anonymous";
        }
        nextImage.onload = () => resolve(nextImage);
        nextImage.onerror = () => reject(new Error("Unable to load image for gradient sampling"));
        nextImage.src = pixelReadableSrc;
      });

      const canvas = document.createElement("canvas");
      const width = 32;
      const height = 20;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context || !image.naturalWidth || !image.naturalHeight) return null;

      context.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        0,
        0,
        width,
        height
      );
      const data = context.getImageData(0, 0, width, height).data;
      const sample = averageImageDataColor(data);
      if (sample) rememberSample(normalizedSrc, sample);
      return sample;
    } catch {
      return null;
    }
  })();

  const timeoutRequest = new Promise((resolve) => {
    timeoutId = window.setTimeout(() => resolve(null), IMAGE_GRADIENT_SAMPLE_TIMEOUT_MS);
  });
  const request = Promise.race([sampleRequest, timeoutRequest]).finally(() => {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    pendingSampleCache.delete(normalizedSrc);
  });

  pendingSampleCache.set(normalizedSrc, request);
  return request;
}

const resolvePaletteState = (src, fallbackRgb, fallbackPalette) => {
  if (!src) {
    return { src, palette: fallbackPalette, ready: true };
  }

  const cachedSample = getCachedImageGradientSample(src);
  if (!cachedSample) {
    return { src, palette: fallbackPalette, ready: false };
  }

  return {
    src,
    palette: buildImageGradientPalette(cachedSample, fallbackRgb),
    ready: true,
  };
};

export function useImageGradientPalette(src, fallbackRgb) {
  const normalizedSrc = normalizeImageSource(src);
  const fallbackRed = Number(fallbackRgb?.r) || 0;
  const fallbackGreen = Number(fallbackRgb?.g) || 0;
  const fallbackBlue = Number(fallbackRgb?.b) || 0;
  const normalizedFallback = useMemo(
    () => ({ r: fallbackRed, g: fallbackGreen, b: fallbackBlue }),
    [fallbackBlue, fallbackGreen, fallbackRed]
  );
  const fallbackPalette = useMemo(
    () => buildImageGradientPalette(normalizedFallback, normalizedFallback),
    [normalizedFallback]
  );
  const [state, setState] = useState(() =>
    resolvePaletteState(normalizedSrc, normalizedFallback, fallbackPalette)
  );

  const currentState =
    state.src === normalizedSrc
      ? state
      : resolvePaletteState(normalizedSrc, normalizedFallback, fallbackPalette);

  useEffect(() => {
    let cancelled = false;
    const immediateState = resolvePaletteState(
      normalizedSrc,
      normalizedFallback,
      fallbackPalette
    );
    setState(immediateState);

    if (!normalizedSrc || immediateState.ready) {
      return () => {
        cancelled = true;
      };
    }

    loadImageGradientSample(normalizedSrc).then((sample) => {
      if (cancelled) return;
      setState({
        src: normalizedSrc,
        palette: sample
          ? buildImageGradientPalette(sample, normalizedFallback)
          : fallbackPalette,
        ready: true,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [fallbackPalette, normalizedFallback, normalizedSrc]);

  return currentState;
}

export function clearImageGradientSampleCache() {
  sampledColorCache.clear();
  pendingSampleCache.clear();
}
